/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { DynForm } from 'src/app/_model/dyn-form';
import {
  DbPersistenceService,
  Query,
} from 'src/app/_services/persistence/db-persistence/db-persistence.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { DynFormDataDaoService } from '../dyn-form-data-dao.service';
import { IMSId, IMSObject, IMSObjectType } from 'src/app/_model/ims-object';
import { DynFormData } from 'src/app/_model/dyn-form-data';
import { BadgesService } from 'src/app/_services/badges/logic/badges.service';
import { DynFormDataDbTableDefinition } from './dyn-form-data-db-table-definition';
import { ActivityConfirmationsDbTableDefinition } from 'src/app/_services/activity-confirmations/dao/activity-confirmations-dao/activity-confirmations-db-table-definition';

@Injectable()
export class DynFormDataDbDaoService implements DynFormDataDaoService {
  constructor(
    private logger: LoggingService,
    private dbPersistenceService: DbPersistenceService,
    private badgesService: BadgesService,
    private dynFormDataTableDefinition: DynFormDataDbTableDefinition,
    // Only for the table name of the "last edited" subquery below - the DAO reads
    // the ACs directly rather than through their service, which would be a
    // dependency cycle (ActivityConfirmationsLogicService already uses DynFormsService).
    private activityConfirmationsTableDefinition: ActivityConfirmationsDbTableDefinition,
  ) {}

  /**
   * SQL for "when was this document last edited, on any device".
   *
   * The local stamp alone is not enough: the same document may have been written on
   * another device since, and only its ActivityConfirmation knows about that. The
   * subquery takes the head of the chain - the same version isCurrentVersion() picks
   * in the TS code, so the entry the draft list marks as "Zuletzt bearbeitet" and the
   * entry a short press opens are the same one.
   *
   * Kept as one expression instead of a computed column, so it can be used in ORDER BY
   * without changing what the SELECT returns.
   */
  private lastEditedTstampSql(): string {
    const dynFormDataTable = this.dynFormDataTableDefinition.tableName;
    const acTable = this.activityConfirmationsTableDefinition.tableName;
    return `MAX(
      COALESCE(${dynFormDataTable}.lastModifiedTstamp, ${dynFormDataTable}.entityCreatedTstamp, 0),
      COALESCE((SELECT ac.entitycreatedtstamp
                  FROM ${acTable} ac
                 WHERE (CASE WHEN ${dynFormDataTable}.chainRootAcId IS NOT NULL
                                   AND ac.chainrootacid IS NOT NULL
                             THEN ac.chainrootacid = ${dynFormDataTable}.chainRootAcId
                             ELSE ac.acukey = ${dynFormDataTable}.acukey END)
                   AND ac.nextversionactconfid IS NULL
                   AND COALESCE(ac.isconflictversion, 0) = 0
                 ORDER BY ac.entitycreatedtstamp DESC
                 LIMIT 1), 0)
    )`;
  }

  // DynFormDataDaoService
  public async saveDynFormData(
    dynFormData: DynFormData,
    dynForm: DynForm,
    obj: IMSObject,
    additionalDataKey: string,
  ): Promise<number> {
    const table = this.dynFormDataTableDefinition.tableName;
    let dynFormDataQuery: Query = {
        statement:
          'INSERT OR REPLACE INTO ' +
          this.dynFormDataTableDefinition.tableName +
          '(id, formId, objRef, objId, isformfinished, ' +
          ' finishRawTstamp, formdata, entityCreatedTstamp, referenceTs, manuallysavedatleastonce, extFileId, origformdata, isDirty,' +
          ' draftOwnerEmpId, draftOwnerMcId, draftOwnerServiceJobId, isRestoredFromDraft, cloudSyncState,' +
          ' acukey, acid, chainRootAcId, acStatus, acIsLocked,' +
          ' restoredFromDraftEmpId, restoredFromDraftTstamp, restoredFromDraftDeviceLabel,' +
          ' lastModifiedTstamp)' +
          ' VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,?,' +
          // APP4-948: the chain identity survives a save that does not know it. Every caller
          // hands over an in-memory DynFormData, and that copy is stale as soon as an upload
          // response wrote the identity via updateACInfo - INSERT OR REPLACE would put the
          // nulls of the stale copy back, and the next upload then started a SECOND document
          // (the submitted 018 next to the orphaned draft 017). The subqueries read the row
          // being replaced; they are evaluated before it is rewritten and yield null for a
          // new row, so an insert is unaffected.
          ' COALESCE((?), (SELECT acukey FROM ' + table + ' WHERE id = (?))),' +
          ' COALESCE((?), (SELECT acid FROM ' + table + ' WHERE id = (?))),' +
          ' COALESCE((?), (SELECT chainRootAcId FROM ' + table + ' WHERE id = (?))),' +
          ' ?,?,?,?,?,?)',
        parameters: [
          dynFormData.formDataId,
          Number(dynForm.id.imsId),
          obj.id.objRef ? obj.id.objRef : undefined,
          dynFormData.objId ?? obj.getDynFormDataKey(additionalDataKey),
          dynFormData.isFormFinished ? 1 : 0,
          dynFormData.finishTs,
          JSON.stringify(dynFormData.formData),
          dynFormData.entityCreatedTstamp
            ? dynFormData.entityCreatedTstamp
            : Date.now(),
          dynFormData.referenceTs,
          dynFormData._formpi_manuallysavedatleastonce,
          dynFormData.extFileId,
          JSON.stringify(dynFormData.origFormData),
          dynFormData.isDirty ?? false,
          dynFormData.draftOwnerEmpId ?? null,
          dynFormData.draftOwnerMcId ?? null,
          dynFormData.draftOwnerServiceJobId ?? null,
          dynFormData.isRestoredFromDraft ? 1 : 0,
          dynFormData.cloudSyncState ?? null,
          // APP4-948: persist the AC identity too — INSERT OR REPLACE would otherwise wipe the
          // values set via updateACInfo on every local save (and restored drafts would lose the
          // acid needed for claim-on-open). Each value is paired with the row id for the
          // COALESCE fallback in the statement above.
          dynFormData.acukey ?? null,
          dynFormData.formDataId ?? null,
          dynFormData.acid ?? null,
          dynFormData.formDataId ?? null,
          dynFormData.chainRootAcId ?? null,
          dynFormData.formDataId ?? null,
          dynFormData.acStatus ?? null,
          dynFormData.acIsLocked ?? null,
          dynFormData.restoredFromDraftEmpId ?? null,
          dynFormData.restoredFromDraftTstamp ?? null,
          dynFormData.restoredFromDraftDeviceLabel ?? null,
          // Every write is an edit on this device - taken as-is rather than from the
          // caller, so no save path can forget to advance it.
          Date.now(),
        ],
      };
    try {
      let db = await this.dbPersistenceService.getImsDb();
      
      await this.dbPersistenceService.executeStatement(db, dynFormDataQuery);
      let res = await this.dbPersistenceService.executeQuery(db, {
        statement: 'select last_insert_rowid() as id',
        parameters: [],
      });
      await this.updateBadgeInfos();
      const id = res.values.shift()?.id;
      return id;
    } catch (error) {
      this.logger.log(
        'Error saving dyn form data to db',
        error,
        '-',
        error?.message,
        dynFormDataQuery
      );
    }
    return undefined;
  }

  public async updateACInfo(
    dynFormDataId: number,
    acukey: string,
    acid: number,
    chainRootAcId?: number,
    acStatus?: number,
    acIsLocked?: boolean,
    draftOwnerEmpId?: number,
    draftOwnerMcId?: number,
    draftOwnerServiceJobId?: number,
    cloudSyncState?: number,
  ): Promise<void> {
    try {
      let db = await this.dbPersistenceService.getImsDb();
      let dynFormDataQuery: Query = {
        statement:
          'UPDATE ' +
          this.dynFormDataTableDefinition.tableName +
          ' ' +
          'SET acukey = (?), ' +
          '    acid = (?), ' +
          // KRK-45: COALESCE, unlike the other columns - the claim-on-open path has no
          // chain root of its own to offer and must not wipe the one the upload stored.
          '    chainRootAcId = COALESCE((?), chainRootAcId), ' +
          '    acStatus = (?), ' +
          '    acIsLocked = (?), ' +
          '    draftOwnerEmpId = (?), ' +
          '    draftOwnerMcId = (?), ' +
          '    draftOwnerServiceJobId = (?), ' +
          '    cloudSyncState = COALESCE((?), cloudSyncState) ' +
          'WHERE id = (?)',
        parameters: [acukey, acid, chainRootAcId ?? null, acStatus ?? null, acIsLocked ?? null, draftOwnerEmpId ?? null, draftOwnerMcId ?? null, draftOwnerServiceJobId ?? null, cloudSyncState ?? null, dynFormDataId],
      };

      await this.dbPersistenceService.executeStatement(db, dynFormDataQuery);
      // The AC identity is one of the markers the badge counts a row by, so the badge of
      // a cloud autosaved draft only becomes correct here - the local save that queued
      // the upload ran before the backend answered with the acukey.
      await this.updateBadgeInfos();
    } catch (error) {
      this.logger.log(
        'Error updateing dyn form data with acukey',
        error,
        '-',
        error?.message,
      );
    }
  }

  public async updateCloudSyncStateByDraft(
    acukey: string,
    acid: number,
    cloudSyncState: number,
    chainRootAcId?: number,
  ): Promise<void> {
    try {
      let db = await this.dbPersistenceService.getImsDb();
      // Matches on any of the three keys, like hasNotFinishedDynFormDataForDraft: every
      // save creates a new AC version, so a pushed acid is often newer than anything
      // stored locally. chainRootAcId is the reliable one (KRK-45), the acukey only a
      // fallback for rows written before it existed. A null parameter never matches in
      // SQL, so passing only some of them narrows this correctly.
      await this.dbPersistenceService.executeStatement(db, {
        statement:
          'UPDATE ' +
          this.dynFormDataTableDefinition.tableName +
          ' SET cloudSyncState = (?)' +
          ' WHERE (chainRootAcId = (?) OR acukey = (?) OR acid = (?))' +
          ' AND isformfinished = 0',
        parameters: [cloudSyncState, chainRootAcId ?? null, acukey ?? null, acid ?? null],
      });
    } catch (error) {
      this.logger.log(
        'Error updating cloudSyncState by acukey/acid',
        error,
        '-',
        error?.message,
      );
    }
  }

  public async getAcIdentity(
    formDataId: number,
  ): Promise<{ acukey?: string; acid?: number; chainRootAcId?: number }> {
    if (formDataId == undefined) {
      return {};
    }
    try {
      let db = await this.dbPersistenceService.getImsDb();
      const res = await this.dbPersistenceService.executeQuery(db, {
        statement:
          'SELECT acukey, acid, chainRootAcId FROM ' +
          this.dynFormDataTableDefinition.tableName +
          ' WHERE id = (?)',
        parameters: [formDataId],
      });
      const row = res.values?.[0];
      return {
        acukey: row?.['acukey'] ?? undefined,
        acid: row?.['acid'] ?? undefined,
        chainRootAcId: row?.['chainRootAcId'] ?? undefined,
      };
    } catch (error) {
      this.logger.log(
        'Error reading AC identity of dyn form data',
        formDataId,
        error,
        '-',
        error?.message,
      );
      return {};
    }
  }

  public async updateIsDirty(formDataId: number, isDirty: boolean): Promise<void> {
    if (formDataId == undefined) {
      return;
    }
    try {
      let db = await this.dbPersistenceService.getImsDb();
      await this.dbPersistenceService.executeStatement(db, {
        statement:
          'UPDATE ' +
          this.dynFormDataTableDefinition.tableName +
          ' SET isDirty = (?) WHERE id = (?)',
        parameters: [isDirty ? 1 : 0, formDataId],
      });
    } catch (error) {
      this.logger.log(
        'Error updating isDirty of dyn form data',
        formDataId,
        error,
        '-',
        error?.message,
      );
    }
  }

  public async hasNotFinishedDynFormDataForDraft(
    acukey: string,
    acid: number,
    chainRootAcId?: number,
  ): Promise<boolean> {
    try {
      let db = await this.dbPersistenceService.getImsDb();
      const res = await this.dbPersistenceService.executeQuery(db, {
        statement:
          'SELECT id FROM ' +
          this.dynFormDataTableDefinition.tableName +
          ' WHERE isformfinished = 0' +
          ' AND (chainRootAcId = (?) OR acukey = (?) OR acid = (?)) LIMIT 1',
        parameters: [chainRootAcId ?? null, acukey ?? null, acid ?? null],
      });
      return res.values.length > 0;
    } catch (error) {
      this.logger.log(
        'Error checking for unfinished dyn form data by acukey/acid',
        error,
        '-',
        error?.message,
      );
      return false;
    }
  }

  /**
   * KRK-45: removes EVERY unfinished local row of the chain, edited ones included.
   *
   * Only for a real deletion (the draft_deleted push): the document is gone on the backend, so
   * there is nothing a queued upload of a dirty row could attach to - it would be stored as a
   * deleted conflict version and the answer would tell this device to drop the row anyway.
   *
   * NOT to be used for the cleanup paths - deleteNotDirtyDraftsForChain is the one that keeps
   * offline work, see the note there.
   */
  public async deleteDraftsForChain(
    chainRootAcId: number | undefined,
    acukey: string | undefined,
  ): Promise<void> {
    if (chainRootAcId == undefined && (acukey == undefined || acukey === '')) {
      return;
    }
    try {
      const db = await this.dbPersistenceService.getImsDb();
      const where =
        ' WHERE (chainRootAcId = (?) OR acukey = (?)) AND isformfinished = 0';
      const whereParams = [chainRootAcId ?? null, acukey ?? null];

      const doomed = await this.dbPersistenceService.executeQuery(db, {
        statement:
          'SELECT id, formId, acid, isDirty FROM ' +
          this.dynFormDataTableDefinition.tableName +
          where,
        parameters: whereParams,
      });
      this.logger.log(
        'deleteDraftsForChain - deleting',
        doomed.values.length,
        'local row(s) of chain root',
        chainRootAcId,
        'acukey',
        acukey,
        JSON.stringify(doomed.values),
      );
      if (doomed.values.length === 0) {
        return;
      }

      await this.dbPersistenceService.executeStatement(db, {
        statement:
          'DELETE FROM ' + this.dynFormDataTableDefinition.tableName + where,
        parameters: whereParams,
      });
      await this.updateBadgeInfos();
    } catch (error) {
      this.logger.log(
        'Error deleting the drafts of chain',
        error,
        '-',
        error?.message,
      );
    }
  }

  public async deleteNotDirtyDraftsForChain(
    chainRootAcId: number | undefined,
    acukey: string | undefined,
  ): Promise<void> {
    if (chainRootAcId == undefined && (acukey == undefined || acukey === '')) {
      return;
    }
    try {
      let db = await this.dbPersistenceService.getImsDb();

      // Deliberately NO `isRestoredFromDraft = 1` here. That flag is only set when a
      // draft was taken over FROM the cloud, so on the device that originally created
      // the draft it is 0 - which left exactly that device with a phantom draft that
      // could never be cleaned up automatically. The chain identity plus "unfinished
      // and not dirty" is the correct scope: the content is safely in the backend.
      //
      // KRK-45: matched on chainRootAcId, with the acukey only as a fallback for rows
      // written before it existed. A smart renumbering rewrites the acukey, so the
      // acukey alone stopped finding the rows of its own chain.
      const where =
        ' WHERE (chainRootAcId = (?) OR acukey = (?)) AND isformfinished = 0' +
        ' AND (isDirty IS NULL OR isDirty = 0)';
      const whereParams = [chainRootAcId ?? null, acukey ?? null];

      // Logged before deleting because executeStatement returns no row count, and the
      // phantom drafts this cleans up were hard to explain after the fact.
      const doomed = await this.dbPersistenceService.executeQuery(db, {
        statement:
          'SELECT id, formId, acid, isRestoredFromDraft FROM ' +
          this.dynFormDataTableDefinition.tableName +
          where,
        parameters: whereParams,
      });
      if (doomed.values.length === 0) {
        // "Nothing to delete" alone does not say whether no row of this chain exists or
        // whether one exists but was excluded - the difference matters and cost real
        // debugging time once, so the candidates are dumped with the deciding columns.
        const candidates = await this.dbPersistenceService.executeQuery(db, {
          statement:
            'SELECT id, isformfinished, isDirty, isRestoredFromDraft FROM ' +
            this.dynFormDataTableDefinition.tableName +
            ' WHERE chainRootAcId = (?) OR acukey = (?)',
          parameters: whereParams,
        });
        this.logger.log(
          'deleteNotDirtyDraftsForChain - nothing to delete for chain root',
          chainRootAcId,
          'acukey',
          acukey,
          '- rows of this chain:',
          JSON.stringify(candidates.values),
        );
        return;
      }
      this.logger.log(
        'deleteNotDirtyDraftsForChain - deleting',
        doomed.values.length,
        'unedited local draft(s) of chain root',
        chainRootAcId,
        'acukey',
        acukey,
        JSON.stringify(doomed.values),
      );

      await this.dbPersistenceService.executeStatement(db, {
        statement:
          'DELETE FROM ' + this.dynFormDataTableDefinition.tableName + where,
        parameters: whereParams,
      });
      await this.updateBadgeInfos();
    } catch (error) {
      this.logger.log(
        'Error deleting drafts of chain',
        error,
        '-',
        error?.message,
      );
    }
  }

  public async deleteDynFormData(dynFormData: DynFormData): Promise<boolean> {
    try {
      let db = await this.dbPersistenceService.getImsDb();

      let conditionParams = [];
      let condition = ' WHERE id = (?)';

      conditionParams.push(dynFormData.formDataId);

      await this.dbPersistenceService.executeStatement(db, {
        statement:
          'DELETE FROM ' +
          this.dynFormDataTableDefinition.tableName +
          condition +
          ';',
        parameters: conditionParams,
      });
      await this.updateBadgeInfos();
      return true;
    } catch (error) {
      this.logger.log(
        'Error deleting dyn form data from db',
        error,
        '-',
        error?.message,
      );
      return false;
    }
  }

  public async hasFinishedNotExpiredFormData(
    dynForm: DynForm,
    obj: IMSObject,
    additionalDataKey: string,
    maxAgeDays: number = 0,
  ): Promise<boolean> {
    this.logger.log(
      this.constructor.name,
      'hasFinishedNotExpiredFormData',
      dynForm.expiration,
      dynForm.id,
      obj.id,
      obj.getDynFormDataKey(additionalDataKey),
    );
    try {
      let db = await this.dbPersistenceService.getImsDb();
      let dynFormData: DynFormData[] = [];

      let conditionParams = [];
      let condition =
        ' WHERE formId = (?) AND objRef = (?) AND objId = (?) AND isformfinished = 1';

      conditionParams.push(dynForm.id.imsId);
      conditionParams.push(obj.id.objRef ? obj.id.objRef : undefined);
      conditionParams.push(obj.getDynFormDataKey(additionalDataKey));

      if (dynForm.expiration === 'REQUEST' || dynForm.expiration === 'DAY') {
        // In case of a simple request expiration check same as day if a form was submitted today
        if (maxAgeDays > 0) {
          condition += ` AND referenceTs >= strftime("%s", "now", "localtime",  "start of day", "-${maxAgeDays} days") *1000`;
        } else {
          condition +=
            ' AND referenceTs >= strftime("%s", "now", "localtime", "start of day") *1000';
        }
      } else if (dynForm.expiration === 'WEEK') {
        condition +=
          ' AND referenceTs >= strftime("%s", "now", "localtime",  "weekday 0", "-6 days") *1000';
      } else if (dynForm.expiration === 'JOB') {
        // No additional condition needed simply search for a finished form data with the obi id
      } else {
        this.logger.log(
          this.constructor.name,
          'expiration',
          dynForm.expiration,
          'not yet implemented',
        );
      }

      this.logger.log(
        this.constructor.name,
        'Check for DynFormData with condition:',
        condition,
        conditionParams,
      );

      let data = await this.dbPersistenceService.executeQuery(db, {
        statement:
          'SELECT * FROM ' +
          this.dynFormDataTableDefinition.tableName +
          condition +
          ' ORDER by referenceTs DESC LIMIT 1;',
        parameters: conditionParams,
      });

      if (data.values.length === 0) {
        this.logger.log(
          this.constructor.name,
          'No finished form data for expiration found',
        );
        return false;
      } else {
        this.logger.log(
          this.constructor.name,
          'Found finished form data for expiration',
        );
        return true;
      }
    } catch (error) {
      this.logger.log(
        'Error reading dynform data from db for expiration check',
        error,
      );
      throw error;
    }
  }

  public async getDynFormData(
    dynForm: DynForm,
    obj: IMSObject,
    additionalDataKey: string,
  ): Promise<DynFormData> {
    try {
      let db = await this.dbPersistenceService.getImsDb();
      let dynFormData: DynFormData[] = [];

      let conditionParams = [];
      let condition = ' WHERE formId = (?) AND objRef = (?) AND objId = (?)';

      conditionParams.push(dynForm.id.imsId);
      conditionParams.push(obj.id.objRef ? obj.id.objRef : undefined);
      conditionParams.push(obj.getDynFormDataKey(additionalDataKey));

      if (dynForm.expiration === 'REQUEST') {
        condition += ' AND isformfinished = 0';
      } else if (dynForm.expiration === 'DAY') {
        condition +=
          ' AND referenceTs >= strftime("%s", "now", "localtime", "start of day") *1000';
      } else if (dynForm.expiration === 'WEEK') {
        condition +=
          ' AND referenceTs >= strftime("%s", "now", "localtime",  "weekday 0", "-6 days") *1000';
      } else if (dynForm.expiration === 'JOB') {
        // No additional condition needed simply search for a finished form data with the obi id
      } else {
        this.logger.log(
          this.constructor.name,
          'expiration',
          dynForm.expiration,
          'not yet implemented',
        );
      }

      this.logger.log(
        this.constructor.name,
        'Get DynFormData with condition:',
        condition,
        conditionParams,
      );

      // Which entry a short press opens: the one that was last edited, NOT the one
      // with the newest reference date. With drafts roaming between devices the
      // newest reference date is regularly not the document the user was last
      // working on - see lastEditedTstampSql(). referenceTs stays as the tie
      // breaker for rows that were never edited after being created.
      let statement =
        `SELECT *, CASE
            WHEN instr(objId, '_js') > 0
            THEN substr(objId, instr(objId, '_js') + 3)
            ELSE NULL
          END AS jsId FROM ${this.dynFormDataTableDefinition.tableName} ${condition}
         ORDER BY isformfinished ASC, ${this.lastEditedTstampSql()} DESC, referenceTs DESC, id DESC LIMIT 1;`;

      let data = await this.dbPersistenceService.executeQuery(db, {
        statement: statement,
        parameters: conditionParams,
      });

      if (data.values.length === 0) {
        // No FormData found - create a new one
        this.logger.log(
          this.constructor.name,
          '- No matching formdata found - create a new one',
        );

        const entityCreatedTstamp = Date.now();
        let insertId = await this.saveDynFormData(
          {
            formDataId: undefined,
            formData: {},
            isFormFinished: false,
            finishTs: undefined,
            entityCreatedTstamp: entityCreatedTstamp,
            referenceTs: entityCreatedTstamp,
          },
          dynForm,
          obj,
          additionalDataKey,
        );
        this.logger.log(
          this.constructor.name,
          '- created formData with id',
          insertId,
        );

        return this.getDynFormData(dynForm, obj, additionalDataKey)
      } else {
        // Found FormData
        this.logger.log(
          this.constructor.name,
          '- Found',
          data.values.length,
          'form datas - try to find a current one.',
        );
        for (let i = 0; i < data.values.length; i++) {
          let row = data.values[i];
          dynFormData.push({
            formDataId: row['id'],
            formData: JSON.parse(row['formdata']),
            isFormFinished: row['isformfinished'] === 1,
            finishTs: row['finishRawTstamp'],
            entityCreatedTstamp: row['entityCreatedTstamp'],
            referenceTs: row['referenceTs'],
            _formpi_manuallysavedatleastonce:
              row['manuallysavedatleastonce'] === 1,
            jobSectionId: row['jsId'],
            extFileId: row['extFileId'],
            acukey: row['acukey'],
            acid: row['acid'],
            chainRootAcId: row['chainRootAcId'] ?? undefined,
            acStatus: row['acStatus'],
            acIsLocked: row['acIsLocked'] === 1 || row['acIsLocked'] === true,
            origFormData: JSON.parse(row['origformdata']),
            isDirty: row['isDirty'] === 1,
            objId: row['objId'],
            objRef: row['objRef'],
            draftOwnerEmpId: row['draftOwnerEmpId'] ?? undefined,
            draftOwnerMcId: row['draftOwnerMcId'] ?? undefined,
            draftOwnerServiceJobId: row['draftOwnerServiceJobId'] ?? undefined,
            cloudSyncState: row['cloudSyncState'] ?? undefined,
            isRestoredFromDraft: row['isRestoredFromDraft'] === 1 || row['isRestoredFromDraft'] === true,
            restoredFromDraftEmpId: row['restoredFromDraftEmpId'] ?? undefined,
            restoredFromDraftTstamp: row['restoredFromDraftTstamp'] ?? undefined,
            restoredFromDraftDeviceLabel:
              row['restoredFromDraftDeviceLabel'] ?? undefined,
            lastModifiedTstamp: row['lastModifiedTstamp'] ?? undefined,
          });
        }

        // TODO: filter if current or new needed
        return dynFormData.shift();
      }
    } catch (error) {
      this.logger.log('Error reading dynform data from db', error);
      throw error;
    }
  }

  public async getSpecificDynFormDataFor(
    dynFormId: IMSId,
    obj: IMSObject,
    objRef: IMSObjectType,
    additionalDataKey: string,
    mode: string,
  ): Promise<DynFormData> {
    try {
      let db = await this.dbPersistenceService.getImsDb();
      let dynFormData: DynFormData[] = [];

      let conditionParams = [];
      let condition = ' WHERE formId = (?)';
      conditionParams.push(
        dynFormId.imsId ? dynFormId.imsId : dynFormId.secondaryId,
      );

      if (obj.id && obj.id.objRef) {
        condition += ' AND objRef = (?) AND objId = (?)';

        conditionParams.push(objRef);
        conditionParams.push(obj.getDynFormDataKey(additionalDataKey));
      }

      if (mode === 'lastfinished') {
        condition +=
          ' AND isformfinished = 1 ORDER by referenceTs DESC LIMIT 1';
      }
      this.logger.log(
        this.constructor.name,
        'get specificDynFormData with condition',
        condition,
        conditionParams,
      );

      let statement =
        `SELECT *, CASE
            WHEN instr(objId, '_js') > 0
            THEN substr(objId, instr(objId, '_js') + 3)
            ELSE NULL
          END AS jsId FROM ${this.dynFormDataTableDefinition.tableName} ${condition}
        ;`;

      let data = await this.dbPersistenceService.executeQuery(db, {
        statement: statement,
        parameters: conditionParams,
      });

      if (data.values.length === 0) {
        // No FormData found - create a new one
        this.logger.log(
          this.constructor.name,
          '- No matching formdata found - return undefined',
        );

        return undefined;
      } else {
        // Found FormData
        this.logger.log(
          this.constructor.name,
          '- Found',
          data.values.length,
          'form datas - try to find a current one.',
        );
        for (let i = 0; i < data.values.length; i++) {
          let row = data.values[i];
          dynFormData.push({
            formDataId: row['id'],
            formData: JSON.parse(row['formdata']),
            isFormFinished: row['isformfinished'] === 1,
            finishTs: row['finishRawTstamp'],
            entityCreatedTstamp: row['entityCreatedTstamp'],
            referenceTs: row['referenceTs'],
            _formpi_manuallysavedatleastonce:
              row['manuallysavedatleastonce'] === 'true' ||
              row['manuallysavedatleastonce'] === true
                ? true
                : false,
            jobSectionId: row['jsId'],
            extFileId: row['extFileId'],
            acukey: row['acukey'],
            acid: row['acid'],
            chainRootAcId: row['chainRootAcId'] ?? undefined,
            acStatus: row['acStatus'],
            acIsLocked: row['acIsLocked'] === 1 || row['acIsLocked'] === true,
            origFormData: JSON.parse(row['origformdata']),
            isDirty: row['isDirty'] === 1,
            objId: row['objId'],
            objRef: row['objRef'],
            draftOwnerEmpId: row['draftOwnerEmpId'] ?? undefined,
            draftOwnerMcId: row['draftOwnerMcId'] ?? undefined,
            draftOwnerServiceJobId: row['draftOwnerServiceJobId'] ?? undefined,
            cloudSyncState: row['cloudSyncState'] ?? undefined,
            isRestoredFromDraft: row['isRestoredFromDraft'] === 1 || row['isRestoredFromDraft'] === true,
            restoredFromDraftEmpId: row['restoredFromDraftEmpId'] ?? undefined,
            restoredFromDraftTstamp: row['restoredFromDraftTstamp'] ?? undefined,
            restoredFromDraftDeviceLabel:
              row['restoredFromDraftDeviceLabel'] ?? undefined,
            lastModifiedTstamp: row['lastModifiedTstamp'] ?? undefined,
          });
        }

        // TODO: filter if current or new needed
        return dynFormData.shift();
      }
    } catch (error) {
      this.logger.log('Error reading dynform data from db', error);
      throw error;
    }
  }

  public async getAllDynFormDatasForObj(
    obj: IMSObject,
  ): Promise<DynFormData[]> {
    try {
      let db = await this.dbPersistenceService.getImsDb();
      let dynFormData: DynFormData[] = [];

      let conditionParams = [];
      let condition = ' WHERE objRef = (?) AND (objId = (?) OR objId LIKE (?))';

      conditionParams.push(obj.id ? obj.id.objRef : undefined);
      conditionParams.push(
        obj.id ? obj.getDynFormDataKey(undefined) : undefined,
      );
      conditionParams.push(obj.id ? obj.getDynFormDataKey('_js%') : undefined);

      let statement =
        `SELECT *, CASE
            WHEN instr(objId, '_js') > 0
            THEN substr(objId, instr(objId, '_js') + 3)
            ELSE NULL
          END AS jsId FROM ${this.dynFormDataTableDefinition.tableName} ${condition}
         ORDER BY entityCreatedTstamp DESC LIMIT 100;`;

      let data = await this.dbPersistenceService.executeQuery(db, {
        statement: statement,
        parameters: conditionParams,
      });
      if (data.values.length === 0) {
        // No FormData found - return empty array
        this.logger.log(
          this.constructor.name,
          '- No matching formdata found - return empty array',
        );

        return [];
      } else {
        // Found FormData
        this.logger.log(
          this.constructor.name,
          '- Found',
          data.values.length,
          'form datas - try to find a current one.',
        );
        for (let i = 0; i < data.values.length; i++) {
          let row = data.values[i];
          dynFormData.push({
            formDataId: row['id'],
            formData: JSON.parse(row['formdata']),
            isFormFinished: row['isformfinished'] === 1,
            finishTs: row['finishRawTstamp'],
            entityCreatedTstamp: row['entityCreatedTstamp'],
            referenceTs: row['referenceTs'],
            _formpi_manuallysavedatleastonce:
              row['manuallysavedatleastonce'] === 'true' ||
              row['manuallysavedatleastonce'] === true
                ? true
                : false,
            dynFormId: row['formId'],
            jobSectionId: row['jsId'],
            extFileId: row['extFileId'],
            acukey: row['acukey'],
            acid: row['acid'],
            chainRootAcId: row['chainRootAcId'] ?? undefined,
            acStatus: row['acStatus'],
            acIsLocked: row['acIsLocked'] === 1 || row['acIsLocked'] === true,
            origFormData: JSON.parse(row['origformdata']),
            isDirty: row['isDirty'] === 1,
            objId: row['objId'],
            objRef: row['objRef'],
            draftOwnerEmpId: row['draftOwnerEmpId'] ?? undefined,
            draftOwnerMcId: row['draftOwnerMcId'] ?? undefined,
            draftOwnerServiceJobId: row['draftOwnerServiceJobId'] ?? undefined,
            cloudSyncState: row['cloudSyncState'] ?? undefined,
            isRestoredFromDraft: row['isRestoredFromDraft'] === 1 || row['isRestoredFromDraft'] === true,
            restoredFromDraftEmpId: row['restoredFromDraftEmpId'] ?? undefined,
            restoredFromDraftTstamp: row['restoredFromDraftTstamp'] ?? undefined,
            restoredFromDraftDeviceLabel:
              row['restoredFromDraftDeviceLabel'] ?? undefined,
            lastModifiedTstamp: row['lastModifiedTstamp'] ?? undefined,
          });
        }

        return dynFormData;
      }
    } catch (error) {
      this.logger.log('Error reading dynform data from db', error);
      throw error;
    }
  }

  public async getNotFinishedDynFormData(
    dynForm: DynForm,
    obj: IMSObject,
    additionalDataKey: string,
  ): Promise<DynFormData[]> {
    try {
      let db = await this.dbPersistenceService.getImsDb();
      let dynFormData: DynFormData[] = [];

      let conditionParams = [];
      let condition =
        ' WHERE formId = (?) AND objRef = (?) AND objId = (?) AND isformfinished = 0';

      conditionParams.push(dynForm.id.imsId);
      conditionParams.push(obj.id ? obj.id.objRef : undefined);
      conditionParams.push(
        obj.id ? obj.getDynFormDataKey(additionalDataKey) : undefined,
      );

      let statement =
        `SELECT *, CASE
            WHEN instr(objId, '_js') > 0
            THEN substr(objId, instr(objId, '_js') + 3)
            ELSE NULL
          END AS jsId FROM ${this.dynFormDataTableDefinition.tableName} ${condition}
        ;`;

      let data = await this.dbPersistenceService.executeQuery(db, {
        statement: statement,
        parameters: conditionParams,
      });

      if (data.values.length === 0) {
        // No FormData found - return empty array
        this.logger.log(
          this.constructor.name,
          '- No matching formdata found - return empty array',
        );

        return [];
      } else {
        // Found FormData
        this.logger.log(
          this.constructor.name,
          '- Found',
          data.values.length,
          'form datas - try to find a current one.',
        );
        for (let i = 0; i < data.values.length; i++) {
          let row = data.values[i];
          dynFormData.push({
            formDataId: row['id'],
            formData: JSON.parse(row['formdata']),
            isFormFinished: row['isformfinished'] === 1,
            finishTs: row['finishRawTstamp'],
            entityCreatedTstamp: row['entityCreatedTstamp'],
            referenceTs: row['referenceTs'],
            _formpi_manuallysavedatleastonce:
              row['manuallysavedatleastonce'] === 'true' ||
              row['manuallysavedatleastonce'] === true
                ? true
                : false,
            jobSectionId: row['jsId'],
            extFileId: row['extFileId'],
            acukey: row['acukey'],
            acid: row['acid'],
            chainRootAcId: row['chainRootAcId'] ?? undefined,
            acStatus: row['acStatus'],
            acIsLocked: row['acIsLocked'] === 1 || row['acIsLocked'] === true,
            origFormData: JSON.parse(row['origformdata']),
            isDirty: row['isDirty'] === 1,
            objId: row['objId'],
            objRef: row['objRef'],
            draftOwnerEmpId: row['draftOwnerEmpId'] ?? undefined,
            draftOwnerMcId: row['draftOwnerMcId'] ?? undefined,
            draftOwnerServiceJobId: row['draftOwnerServiceJobId'] ?? undefined,
            cloudSyncState: row['cloudSyncState'] ?? undefined,
            isRestoredFromDraft: row['isRestoredFromDraft'] === 1 || row['isRestoredFromDraft'] === true,
            restoredFromDraftEmpId: row['restoredFromDraftEmpId'] ?? undefined,
            restoredFromDraftTstamp: row['restoredFromDraftTstamp'] ?? undefined,
            restoredFromDraftDeviceLabel:
              row['restoredFromDraftDeviceLabel'] ?? undefined,
            lastModifiedTstamp: row['lastModifiedTstamp'] ?? undefined,
          });
        }

        // TODO: filter if current or new needed
        return dynFormData;
      }
    } catch (error) {
      this.logger.log('Error reading dynform data from db', error);
      throw error;
    }
  }

  public async getNotFinishedJobSectionDynFormData(
    dynForm: DynForm,
    obj: IMSObject,
  ): Promise<DynFormData[]> {
    try {
      let db = await this.dbPersistenceService.getImsDb();
      let dynFormData: DynFormData[] = [];

      let conditionParams = [];
      let condition =
        ' WHERE formId = (?) AND objRef = (?) AND objId LIKE (?) AND isformfinished = 0';

      conditionParams.push(dynForm.id.imsId);
      conditionParams.push(obj.id ? obj.id.objRef : undefined);
      conditionParams.push(obj.id ? '' + obj.getImsId() + '_js%' : undefined);

      let statement =
        `SELECT *, CASE
            WHEN instr(objId, '_js') > 0
            THEN substr(objId, instr(objId, '_js') + 3)
            ELSE NULL
          END AS jsId FROM ${this.dynFormDataTableDefinition.tableName} ${condition}
        ;`;

      let data = await this.dbPersistenceService.executeQuery(db, {
        statement: statement,
        parameters: conditionParams,
      });

      if (data.values.length === 0) {
        // No FormData found - return empty array
        this.logger.log(
          this.constructor.name,
          '- No matching formdata found - return empty array',
        );

        return [];
      } else {
        // Found FormData
        this.logger.log(
          this.constructor.name,
          '- Found',
          data.values.length,
          'form datas - try to find a current one.',
        );
        for (let i = 0; i < data.values.length; i++) {
          let row = data.values[i];
          dynFormData.push({
            formDataId: row['id'],
            formData: JSON.parse(row['formdata']),
            isFormFinished: row['isformfinished'] === 1,
            finishTs: row['finishRawTstamp'],
            entityCreatedTstamp: row['entityCreatedTstamp'],
            referenceTs: row['referenceTs'],
            _formpi_manuallysavedatleastonce:
              row['manuallysavedatleastonce'] === 'true' ||
              row['manuallysavedatleastonce'] === true
                ? true
                : false,
            jobSectionId: row['jsId'],
            extFileId: row['extFileId'],
            acukey: row['acukey'],
            acid: row['acid'],
            chainRootAcId: row['chainRootAcId'] ?? undefined,
            acStatus: row['acStatus'],
            acIsLocked: row['acIsLocked'] === 1 || row['acIsLocked'] === true,
            origFormData: JSON.parse(row['origformdata']),
            isDirty: row['isDirty'] === 1,
            objId: row['objId'],
            objRef: row['objRef'],
            draftOwnerEmpId: row['draftOwnerEmpId'] ?? undefined,
            draftOwnerMcId: row['draftOwnerMcId'] ?? undefined,
            draftOwnerServiceJobId: row['draftOwnerServiceJobId'] ?? undefined,
            cloudSyncState: row['cloudSyncState'] ?? undefined,
            isRestoredFromDraft: row['isRestoredFromDraft'] === 1 || row['isRestoredFromDraft'] === true,
            restoredFromDraftEmpId: row['restoredFromDraftEmpId'] ?? undefined,
            restoredFromDraftTstamp: row['restoredFromDraftTstamp'] ?? undefined,
            restoredFromDraftDeviceLabel:
              row['restoredFromDraftDeviceLabel'] ?? undefined,
            lastModifiedTstamp: row['lastModifiedTstamp'] ?? undefined,
          });
        }

        return dynFormData;
      }
    } catch (error) {
      this.logger.log('Error reading dynform data from db', error);
      throw error;
    }
  }

  public async getAmountOfFinishedDynFormDatasForObjIdSince(
    dynFormId: IMSId,
    obj: IMSObject,
    additionalDataKey: string,
    timestamp: number,
  ): Promise<number> {
    try {
      let db = await this.dbPersistenceService.getImsDb();

      let conditionParams = [];
      let condition =
        ' WHERE formId = (?) AND objRef = (?) AND objId = (?) AND referenceTs > (?) AND isformfinished = 1';

      conditionParams.push(dynFormId.imsId);
      conditionParams.push(obj.id.objRef ? obj.id.objRef : undefined);
      conditionParams.push(obj.getDynFormDataKey(additionalDataKey));
      conditionParams.push(timestamp);

      let statement =
        'SELECT COUNT(*) as amount FROM ' +
        this.dynFormDataTableDefinition.tableName +
        condition +
        ';';

      let data = await this.dbPersistenceService.executeQuery(db, {
        statement: statement,
        parameters: conditionParams,
      });

      console.log(
        'JBCountSQL',
        data,
        data.values,
        data.values?.[0]?.['amount'],
        isNaN(data.values?.[0]?.['amount']),
        isNaN(+data.values?.[0]?.['amount']),
      );
      if (isNaN(data.values?.[0]?.['amount'])) {
        return undefined;
      }
      return +data.values?.[0]?.['amount'];
    } catch (error) {
      this.logger.log('Error reading dynform data from db', error);
      throw error;
    }
  }

  public async createNewFormData(
    dynForm: DynForm,
    obj: IMSObject,
    additionalDataKey: string,
  ): Promise<DynFormData> {
    try {
      let entityCreatedTstamp = Date.now();
      let insertId = await this.saveDynFormData(
        {
          formDataId: undefined,
          formData: {},
          isFormFinished: false,
          entityCreatedTstamp: entityCreatedTstamp,
          finishTs: undefined,
          // ReferenceTs is default the entity created ts
          referenceTs: entityCreatedTstamp,
        },
        dynForm,
        obj,
        additionalDataKey,
      );
      return {
        formDataId: insertId,
        formData: undefined,
        isFormFinished: false,
        finishTs: undefined,
        entityCreatedTstamp: entityCreatedTstamp,
        referenceTs: entityCreatedTstamp,
      };
    } catch (error) {
      this.logger.log('Error reading dynform data from db', error);
      throw error;
    }
  }

  public async getDynFormDataByExtFileId(
    extFileId: string,
  ): Promise<DynFormData | undefined> {
    try {
      let db = await this.dbPersistenceService.getImsDb();
      let data = await this.dbPersistenceService.executeQuery(db, {
        statement: `SELECT * FROM ${this.dynFormDataTableDefinition.tableName} WHERE extFileId = (?) LIMIT 1`,
        parameters: [extFileId],
      });
      if (data.values.length === 0) {
        return undefined;
      }
      const row = data.values[0];
      return {
        formDataId: row['id'],
        formData: JSON.parse(row['formdata']),
        isFormFinished: row['isformfinished'] === 1,
        finishTs: row['finishRawTstamp'],
        entityCreatedTstamp: row['entityCreatedTstamp'],
        referenceTs: row['referenceTs'],
        _formpi_manuallysavedatleastonce: row['manuallysavedatleastonce'] === 1 || row['manuallysavedatleastonce'] === true,
        extFileId: row['extFileId'],
        acukey: row['acukey'],
        acid: row['acid'],
        chainRootAcId: row['chainRootAcId'] ?? undefined,
        acStatus: row['acStatus'],
        acIsLocked: row['acIsLocked'] === 1 || row['acIsLocked'] === true,
        origFormData: JSON.parse(row['origformdata']),
        isDirty: row['isDirty'] === 1,
        objId: row['objId'],
        objRef: row['objRef'],
        draftOwnerEmpId: row['draftOwnerEmpId'] ?? undefined,
        draftOwnerMcId: row['draftOwnerMcId'] ?? undefined,
        isRestoredFromDraft: row['isRestoredFromDraft'] === 1 || row['isRestoredFromDraft'] === true,
        restoredFromDraftEmpId: row['restoredFromDraftEmpId'] ?? undefined,
        restoredFromDraftTstamp: row['restoredFromDraftTstamp'] ?? undefined,
        restoredFromDraftDeviceLabel:
          row['restoredFromDraftDeviceLabel'] ?? undefined,
        lastModifiedTstamp: row['lastModifiedTstamp'] ?? undefined,
      };
    } catch (error) {
      this.logger.log('Error reading dynform data by extFileId from db', error);
      throw error;
    }
  }

  public async getDynFormDataByFormIdAndReferenceTs(
    formId: number,
    referenceTs: number,
  ): Promise<DynFormData | undefined> {
    try {
      let db = await this.dbPersistenceService.getImsDb();
      let data = await this.dbPersistenceService.executeQuery(db, {
        statement: `SELECT * FROM ${this.dynFormDataTableDefinition.tableName} WHERE formId = (?) AND referenceTs = (?) LIMIT 1`,
        parameters: [formId, referenceTs],
      });
      if (data.values.length === 0) {
        return undefined;
      }
      const row = data.values[0];
      return {
        formDataId: row['id'],
        formData: JSON.parse(row['formdata']),
        isFormFinished: row['isformfinished'] === 1,
        finishTs: row['finishRawTstamp'],
        entityCreatedTstamp: row['entityCreatedTstamp'],
        referenceTs: row['referenceTs'],
        extFileId: row['extFileId'],
        isDirty: row['isDirty'] === 1,
        objId: row['objId'],
        objRef: row['objRef'],
        draftOwnerEmpId: row['draftOwnerEmpId'] ?? undefined,
        draftOwnerMcId: row['draftOwnerMcId'] ?? undefined,
        isRestoredFromDraft: row['isRestoredFromDraft'] === 1 || row['isRestoredFromDraft'] === true,
        restoredFromDraftEmpId: row['restoredFromDraftEmpId'] ?? undefined,
        restoredFromDraftTstamp: row['restoredFromDraftTstamp'] ?? undefined,
        restoredFromDraftDeviceLabel:
          row['restoredFromDraftDeviceLabel'] ?? undefined,
        lastModifiedTstamp: row['lastModifiedTstamp'] ?? undefined,
      };
    } catch (error) {
      this.logger.log('Error reading dynform data by formId+referenceTs from db', error);
      throw error;
    }
  }

  // Resetable
  public async reset() {
    // On a reset all dynform data should be lost so technically a form can be
    // filled again
    await this.clear();
  }

  public async clear() {
    let db = await this.dbPersistenceService.getImsDb();
    this.logger.log(
      this.constructor.name,
      'truncate table',
      this.dynFormDataTableDefinition.tableName,
    );
    await this.dbPersistenceService.truncateTable(
      db,
      this.dynFormDataTableDefinition.tableName,
    );
  }

  // Private Functions

  private async updateBadgeInfos() {
    this.logger.log(this.constructor.name, 'Update Badge Info');
    await this.updateMultiInstanceAmountBadge();
    await this.updateJobSectionInstanceAmountBadge();
  }

  private async updateMultiInstanceAmountBadge() {
    // isDirty alone is not enough since APP4-948: a cloud autosave hands the payload to
    // the upload queue and clears the flag (see queueCloudAutosave), so every draft that
    // reached the cloud dropped out of the badge while its row is still listed in the
    // draft modal. The other markers say "this row carries actual work" just as well:
    // it belongs to an AC chain (acukey/acid), it came from a draft, or it was saved by
    // hand. Untouched empty rows still have none of them, which is what LBW-229 asked
    // for - and they are removed on leaving the form anyway.
    // Not covered: the very first autosave of a form while offline, which has no AC
    // identity yet. Its badge appears once the queued upload is confirmed, see
    // updateACInfo.
    const query =
      'SELECT formId, objRef, objId, COUNT(*) as amount FROM dynFormData' +
      ' WHERE isformfinished = 0' +
      ' AND (isDirty = 1 OR acukey IS NOT NULL OR acid IS NOT NULL' +
      '      OR isRestoredFromDraft = 1 OR manuallysavedatleastonce = 1)' +
      ' GROUP BY formId, objRef, objId';
    try {
      let db = await this.dbPersistenceService.getImsDb();

      this.logger.log(this.constructor.name, 'updateMultiInstanceAmountBadge');
      let data = await this.dbPersistenceService.executeQuery(db, {
        statement: query,
        parameters: [],
      });

      if (data.values.length === 0) {
        this.badgesService.setMultiInstanceAmountBadge({});
      } else {
        let badgesInfo = {};
        for (let i = 0; i < data.values.length; i++) {
          let row = data.values[i];
          let formId = row['formId'];
          let objRef = row['objRef'];
          let objid = row['objId'];

          let badgesKey =
            formId + ';' + (objRef ? objRef : '') + ';' + (objid ? objid : '');

          badgesInfo[badgesKey] = row['amount'];
        }

        this.logger.log(
          this.constructor.name,
          'Update MultiInstance Badge Info',
        );
        await this.badgesService.setMultiInstanceAmountBadge(badgesInfo);
      }
    } catch (error) {
      this.logger.log('Error reading dyn form multi instance amount', error);
      throw error;
    }
  }

  private async updateJobSectionInstanceAmountBadge() {
    const query =
      "SELECT *, Count(*) as amount, substr(objId, 0, instr(objId, '_js')) as rawObjId  from dynFormData WHERE isformfinished = 0 and objId LIKE '%_js%' Group by rawObjId";
    try {
      let db = await this.dbPersistenceService.getImsDb();

      this.logger.log(this.constructor.name, 'Update JobSection Badge Info');
      let data = await this.dbPersistenceService.executeQuery(db, {
        statement: query,
        parameters: [],
      });

      if (data.values.length === 0) {
        await this.badgesService.setJobSectionInstanceAmountBadge({});
      } else {
        let badgesInfo = {};
        for (let i = 0; i < data.values.length; i++) {
          let row = data.values[i];
          let formId = row['formId'];
          let objRef = row['objRef'];
          let objid = row['objId'];
          
          let badgesKey =
            formId + ';' + (objRef ? objRef : '') + ';' + (objid ? objid : '');

          badgesInfo[badgesKey] = row['amount'];
        }

        this.logger.log(
          this.constructor.name,
          'Update JobSection Badge Info with:',
          badgesInfo,
        );
        await this.badgesService.setJobSectionInstanceAmountBadge(badgesInfo);
      }
    } catch (error) {
      this.logger.log('Error reading dyn form multi instance amount', error);
      throw error;
    }
  }
}
